/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file sets up the logic for retrieving an individual patient.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to create logic based side effects for the redux store.
 */
import { createLogic } from 'redux-logic';

/**
 * Typings
 */
import { TypedUseSelectorHook, useSelector } from 'react-redux';

/**
 * Utility library for reducing redux boilerplate while remaining typed. 
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to register the logics so that they are included in the store. 
 */
import reducerRegistry from '../ReducerRegistry';

/*
 * Used to get access to the API types and requests
 */
import * as Dtos from '../../api/dtos';

/**
 * Enumeration used to determine the state of a request.
 */
import { IRequestState, RequestState } from '.';

/**
 * Used to create a get request to the servicestack API.
 */
import { createGetRequest } from '../../utilities/createRequest';

/**
 * Typings
 */
import { ILogicSideEffect } from '../../utilities/logicTypes';
import client from '../../api/client';
import { IAuthSession } from '@servicestack/client';

/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/

interface IInitialAuthenticationState {
    user: IAuthSession | null;
    loadState: IRequestState;
}

export interface IAuthenticationState {
    authentication: IInitialAuthenticationState;
}

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const AuthenticationInitialState: IInitialAuthenticationState = {
    user: null,
    loadState: {
        state: RequestState.None
    }
};

/*
* ---------------------------------------------------------------------------------
* Selectors
* ---------------------------------------------------------------------------------
*/

export const useAuthenticationSelector: TypedUseSelectorHook<IAuthenticationState> = useSelector;

export const AuthenticationSelectors = {
    selectUser: (state: IAuthenticationState) => state.authentication.user,
    selectLoadState: (state: IAuthenticationState) => state.authentication.loadState
};

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class AuthenticationReducer extends ImmerReducer<IInitialAuthenticationState> {
    public checkAuthentication() {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public signoutAuthentication() {
        this.draftState.user = this.draftState.user;
    }

    public loadSuccess(user: IAuthSession) {
        this.draftState.user = user;
        this.draftState.loadState = {
            state: RequestState.Success
        };
    }

    public loadFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.user = null;
        this.draftState.loadState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public signoutSuccess() {
        this.draftState.user = null;
        this.draftState.loadState = {
            state: RequestState.Success
        };
    }

    public signoutFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.user = null;
        this.draftState.loadState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public clear() {
        return { ...AuthenticationInitialState };
    }
}

export const AuthenticationActions = createActionCreators(AuthenticationReducer);
const reducer = createReducerFunction(AuthenticationReducer, AuthenticationInitialState);

/*
 * ---------------------------------------------------------------------------------
 * API
 * ---------------------------------------------------------------------------------
 */

export const AuthenticationApi = {
    checkAuthentication: () => {
        return client.post(new Dtos.Authenticate());
    },
    signoutAuthentication: () => {
        return client.post(new Dtos.Authenticate({ provider: 'logout' }));
    }
}

/*
 * ---------------------------------------------------------------------------------
 * Logic
 * ---------------------------------------------------------------------------------
 */

const AuthenticationLogic = {
    checkAuthentication: createLogic({
        type: AuthenticationActions.checkAuthentication.type,
        latest: true,
        process: async ({ action }: ILogicSideEffect<IAuthenticationState, typeof AuthenticationActions.checkAuthentication>, dispatch, done) => {
            try {
                const response = await AuthenticationApi.checkAuthentication();
                dispatch(AuthenticationActions.loadSuccess(
                    response
                ));
            }
            catch (error) {
                dispatch(AuthenticationActions.loadFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    }),
    signoutAuthentication: createLogic({
        type: AuthenticationActions.signoutAuthentication.type,
        latest: true,
        process: async ({ action }: ILogicSideEffect<IAuthenticationState, typeof AuthenticationActions.signoutAuthentication>, dispatch, done) => {
            try {
                const response = await AuthenticationApi.signoutAuthentication();
                dispatch(AuthenticationActions.signoutSuccess());
            }
            catch (error) {
                dispatch(AuthenticationActions.signoutFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    })
};

const logics = [
    AuthenticationLogic.checkAuthentication
]

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('authentication', reducer, logics);


// WEBPACK FOOTER //
// ./src/store/modules/authentication.ts